

import { MapPin, Paperclip, ExternalLink } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR, es, enUS } from "date-fns/locale";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Meeting } from "../../../services/meeting.service";
import type { Project } from "../../../services/project.service";
import type { Participante } from "../../../services/participante.service";
import * as customizationService from "../../../services/customization.service";

interface AtaHeaderProps {
  meeting: Meeting;
  project: Project;
  participantes: Participante[];
}

export function AtaHeader({
  meeting,
  project,
  participantes,
}: AtaHeaderProps) {
  const [headerImageUrl, setHeaderImageUrl] = useState<string | null>(null);
  const { t, i18n } = useTranslation('common');

  const anexos = meeting.anexos;
  const responsavelNome =
    meeting.responsavel_id !== null && meeting.responsavel_id !== undefined
      ? meeting.responsavel_nome
      : null;

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'es': return es;
      case 'en': return enUS;
      default: return ptBR;
    }
  };

  useEffect(() => {
    const fetchCustomization = async () => {
      try {
        const customization = await customizationService.getUserCustomization();
        setHeaderImageUrl(customization.header_image_url);
      } catch (error) {
        console.error("Erro ao carregar personalização:", error);
      }
    };

    fetchCustomization();
  }, []);

  return (
    <header className="mb-6 border-b border-gray-300 pb-4">
      {}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {t('meeting_minutes')} {meeting.number} | {project.name}
          </h1>
          <div className="text-sm text-gray-600">
            {t('meeting_date')} | {format(parseISO(meeting.date), "dd 'de' MMMM 'de' yyyy", { locale: getDateLocale() })}
            {meeting.hora_inicio && (
              <span>
                {' '}&mdash; <a>{t('editor.meeting_start_time')}:</a> {meeting.hora_inicio}
                {meeting.hora_fim && <span> &ndash;  <a>{t('editor.meeting_end_time')}:</a> {meeting.hora_fim}</span>}
              </span>
            )}
          </div>
          {(meeting.legenda || meeting.observacoes || responsavelNome) && (
            <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
              {meeting.legenda && (
                <div className="flex min-w-0 gap-1">
                  <dt className="shrink-0 text-xs font-semibold uppercase">{t('editor.header_legend')}:</dt>
                  <dd className="break-words">{meeting.legenda}</dd>
                </div>
              )}
              {meeting.observacoes && (
                <div className="flex min-w-0 gap-1">
                  <dt className="shrink-0 text-xs font-semibold uppercase">{t('editor.header_notes')}:</dt>
                  <dd className="break-words">{meeting.observacoes}</dd>
                </div>
              )}
              {responsavelNome && (
                <div className="flex min-w-0 gap-1">
                  <dt className="shrink-0 text-xs font-semibold uppercase">{t('editor.header_responsible')}:</dt>
                  <dd className="break-words">{responsavelNome}</dd>
                </div>
              )}
            </dl>
          )}
        </div>

        {}
        {headerImageUrl && (
          <div className="ml-4">
            <img
              src={headerImageUrl}
              alt={t('editor.header_logo')}
              className="h-20 w-auto max-w-[300px] object-contain"
            />
          </div>
        )}
      </div>

      {}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {}
        {meeting.location && (
          <div className="bg-gray-50 p-3 rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="text-gray-700" size={16} />
              <span className="text-xs text-gray-600 uppercase font-semibold">{t('location')}</span>
            </div>
            <div className="text-sm font-medium">{meeting.location}</div>
          </div>
        )}

        {}
        {participantes.length > 0 && (
          <div className="bg-gray-50 p-3 rounded-md">
            <div className="text-xs text-gray-600 uppercase font-semibold mb-2">
              {t('participants')} ({participantes.length})
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {participantes.map((p) => (
                <div key={p.id} className="flex items-center gap-1.5 text-sm">
                  <div className="w-1.5 h-1.5 bg-gray-700 rounded-full flex-shrink-0" />
                  <span className="font-medium text-gray-900">{p.nome}</span>
                  {p.empresa && <span className="text-gray-500">({p.empresa})</span>}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* Anexos */}
      {anexos.length > 0 && (
        <div className="mt-4 bg-gray-50 p-3 rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <Paperclip className="text-gray-700" size={16} />
            <span className="text-xs text-gray-600 uppercase font-semibold">{t('editor.attachments')}</span>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            {anexos.map((item, idx) => (
              <div key={idx} className="flex items-center gap-1.5 text-sm">
                <div className="w-1.5 h-1.5 bg-gray-700 rounded-full flex-shrink-0" />
                {item.url ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center gap-1"
                  >
                    {item.nome}
                    <ExternalLink size={11} className="inline" />
                  </a>
                ) : (
                  <span className="text-gray-900">{item.nome}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
